import { CheckCircle2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useContactForm } from "@/components/contact/useContactForm";
import { STORE } from "@/constants/testIds";

const SuccessState = ({ onReset }) => (
  <div
    data-testid={STORE.contactSuccess}
    className="flex flex-col items-center justify-center py-10 text-center"
  >
    <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[color:rgba(31,138,91,0.12)] text-[#1F8A5B]">
      <CheckCircle2 className="h-7 w-7" />
    </span>
    <h2 className="mt-4 font-[var(--font-heading)] text-xl font-semibold text-[var(--ibp-ink)]">
      Thanks — your message was sent.
    </h2>
    <p className="mt-2 max-w-sm text-sm text-[color:rgba(11,18,32,0.68)]">
      We’ll reply by email as soon as we can. You can send another message any
      time.
    </p>
    <button
      type="button"
      onClick={onReset}
      className="mt-6 inline-flex min-h-11 items-center justify-center rounded-xl border border-[color:rgba(10,27,51,0.18)] bg-white px-5 py-3 text-sm font-semibold text-[var(--ibp-ink)] transition-colors hover:bg-[var(--ibp-offwhite)]"
    >
      Send another message
    </button>
  </div>
);

const Field = ({ label, error, errorTestId, children }) => (
  <div>
    <Label className="text-sm font-medium text-[var(--ibp-ink)]">{label}</Label>
    <div className="mt-1.5">{children}</div>
    {error && (
      <p data-testid={errorTestId} className="mt-1.5 text-xs text-[#B42318]">
        {error}
      </p>
    )}
  </div>
);

export const ContactForm = () => {
  const { form, errors, submitting, submitted, update, onSubmit, reset } =
    useContactForm();

  return (
    <div className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.06)] sm:p-8">
      {submitted ? (
        <SuccessState onReset={reset} />
      ) : (
        <form
          data-testid={STORE.contactForm}
          onSubmit={onSubmit}
          noValidate
          className="space-y-5"
        >
          <div className="hidden" aria-hidden="true">
            <Label htmlFor="company">Company</Label>
            <Input
              id="company"
              name="company"
              tabIndex={-1}
              autoComplete="off"
              value={form.company}
              onChange={update("company")}
            />
          </div>
          <Field label="Name" error={errors.name} errorTestId={STORE.contactError}>
            <Input
              id="name"
              data-testid={STORE.contactName}
              value={form.name}
              onChange={update("name")}
              placeholder="Your name"
              className="h-11 rounded-xl"
            />
          </Field>
          <Field label="Email" error={errors.email}>
            <Input
              id="email"
              type="email"
              data-testid={STORE.contactEmail}
              value={form.email}
              onChange={update("email")}
              placeholder="you@example.com"
              className="h-11 rounded-xl"
            />
          </Field>
          <Field label="Message" error={errors.message}>
            <Textarea
              id="message"
              data-testid={STORE.contactMessage}
              value={form.message}
              onChange={update("message")}
              placeholder="How can we help?"
              rows={6}
              className="rounded-xl"
            />
          </Field>
          <button
            type="submit"
            data-testid={STORE.contactSubmit}
            disabled={submitting}
            className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-[var(--ibp-gold-500)] px-5 py-3 text-sm font-semibold text-[var(--ibp-ink)] shadow-[0_14px_30px_rgba(214,162,30,0.18)] transition-colors hover:bg-[var(--ibp-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ibp-focus-ring)] focus-visible:ring-offset-2 disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Send message"}
          </button>
        </form>
      )}
    </div>
  );
};

export default ContactForm;
